import { Fragment, ResolvedPos, type Node as PmNode } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { NodeSelection, Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Node, ReactNodeViewRenderer, mergeAttributes } from '@tiptap/react';
import ZyDefnNodeView from './zydefn-inline-node-view';

const zydefnPluginKey = new PluginKey('zydefnPlugin');

function mapPosThroughTransactions(pos: number, transactions: readonly Transaction[], assoc: -1 | 1 = -1): number {
    let mapped = pos;
    for (const tr of transactions) {
        mapped = tr.mapping.map(mapped, assoc);
    }
    return mapped;
}

function findNodeDepthByName($pos: any, nodeName: string): number | null {
    for (let depth = $pos.depth; depth > 0; depth--) {
        if ($pos.node(depth)?.type?.name === nodeName) {
            return depth;
        }
    }
    return null;
}

function transactionInsertsBlock(tr: Transaction): boolean {
    const steps = tr.steps as any[];
    for (const step of steps) {
        const slice = step?.slice;
        const content = slice?.content;
        if (!content?.descendants) continue;

        let found = false;
        content.descendants((node: any) => {
            if (node?.isBlock) {
                found = true;
                return false;
            }
            return true;
        });

        if (found) return true;
    }
    return false;
}

function transactionInsertsBlockInRange(tr: Transaction, rangeStart: number, rangeEnd: number): boolean {
    const steps = tr.steps as any[];
    for (const step of steps) {
        const slice = step?.slice;
        const content = slice?.content;
        if (!content?.descendants) continue;

        let found = false;
        content.descendants((node: any) => {
            if (node?.isBlock) {
                found = true;
                return false;
            }
            return true;
        });

        if (!found) continue;

        const rawFrom: unknown = step?.from;
        if (typeof rawFrom === 'number' && rawFrom >= rangeStart && rawFrom <= rangeEnd) {
            return true;
        }
    }
    return false;
}

type InsertedBlockCandidate = { pos: number; node: any };

function collectInsertedBlockCandidates(transactions: readonly Transaction[], newState: any): InsertedBlockCandidate[] {
    // Heuristic: find replace-steps that inserted block content, then map their `from`
    // to a position in `newState` where a corresponding block node should start.
    const candidates: InsertedBlockCandidate[] = [];

    for (let ti = 0; ti < transactions.length; ti++) {
        const tr = transactions[ti] as any;
        const steps = tr.steps as any[];

        for (const step of steps) {
            const slice = step?.slice;
            const content = slice?.content;
            if (!content?.descendants) continue;

            let sliceHasBlock = false;
            content.descendants((node: any) => {
                if (node?.isBlock) {
                    sliceHasBlock = true;
                    return false;
                }
                return true;
            });
            if (!sliceHasBlock) continue;

            const rawFrom: unknown = step?.from;
            if (typeof rawFrom !== 'number') continue;

            // Map to post-transaction coords, biased left to land at the inserted content start.
            let mappedPos: number = tr.mapping.map(rawFrom, -1);
            // Then map through subsequent transactions.
            for (let tj = ti + 1; tj < transactions.length; tj++) {
                mappedPos = transactions[tj].mapping.map(mappedPos, -1);
            }

            const nodeAt = newState.doc.nodeAt(mappedPos);
            if (nodeAt?.isBlock) {
                candidates.push({ pos: mappedPos, node: nodeAt });
            }
        }
    }

    // Deduplicate by position (multiple steps can map to the same insertion start).
    const deduped = new Map<number, InsertedBlockCandidate>();
    for (const c of candidates) {
        if (!deduped.has(c.pos)) deduped.set(c.pos, c);
    }

    return Array.from(deduped.values()).sort((a, b) => a.pos - b.pos);
}

type ZyDefnBlockCandidate = { pos: number; node: any };

function collectDowngradeCandidates(state: any): ZyDefnBlockCandidate[] {
    const results: ZyDefnBlockCandidate[] = [];

    state.doc.descendants((node: any, pos: number) => {
        if (node?.type?.name !== 'zydefnBlock') return true;

        // Downgrade rule (per plan + choices):
        // - Only downgrade when it has *exactly one* paragraph child.
        // - Do not downgrade when multiple paragraphs or any other block children exist.
        if (node.childCount === 1 && node.child(0)?.type?.name === 'paragraph') {
            results.push({ pos, node });
        }
        return true;
    });

    return results;
}

export interface ZyDefnAttributes {
    definition: string;
    terms: string[];
}

export interface InsertDefinitionOptions {
    terms: string[];
    definition: string;
}

export interface InsertDefinitionWithContentOptions {
    definition?: string;
    content?: any[];
}

function stripZydefTermsFromFragment(frag: Fragment): Fragment {
    const parts: PmNode[] = [];
    frag.forEach((node: PmNode) => {
        if (node.type.name === 'zydefTerm') {
            stripZydefTermsFromFragment(node.content).forEach(n => {
                parts.push(n);
            });
        } else if (node.content.size) {
            parts.push(node.copy(stripZydefTermsFromFragment(node.content)));
        } else {
            parts.push(node);
        }
    });
    return Fragment.fromArray(parts);
}

function stripZydefTermsFromNode(node: PmNode): PmNode {
    if (node.content.size) {
        return node.copy(stripZydefTermsFromFragment(node.content));
    }
    return node;
}

function findInnermostZyDefnAtSelection($from: ResolvedPos): { start: number; end: number; node: PmNode } | null {
    for (let depth = $from.depth; depth > 0; depth--) {
        const n = $from.node(depth);
        const name = n.type.name;
        if (name === 'zydefn' || name === 'zydefnBlock') {
            const start = $from.before(depth);
            return { start, end: start + n.nodeSize, node: n };
        }
    }
    return null;
}

/**
 * True when inline `zydefn` has no real content: empty, or only U+200B/whitespace text. Any other
 * inline (zydefTerm, inlineMath, etc.) is treated as not empty.
 */
function isTrulyEmptyInlineZydefn(node: PmNode): boolean {
    if (node.type.name !== 'zydefn') return false;
    if (node.content.size === 0) return true;
    let allPlaceholderText = true;
    node.content.forEach((child: PmNode) => {
        if (child.isText) {
            const t = child.text ?? '';
            if (t.replaceAll('\u200B', '').trim() !== '') {
                allPlaceholderText = false;
            }
        } else {
            allPlaceholderText = false;
        }
    });
    return allPlaceholderText;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zydefn: {
            insertDefinition: (options: InsertDefinitionOptions) => ReturnType;
            insertDefinitionWithContent: (options: InsertDefinitionWithContentOptions) => ReturnType;
            removeEmptyInlineZydefn: () => ReturnType;
            removeZyDefn: () => ReturnType;
        };
    }
}

export const ZyDefnHeadless = Node.create({
    name: 'zydefn',

    group: 'inline',

    inline: true,

    content: 'inline*',

    addAttributes() {
        return {
            terms: {
                default: '',
                parseHTML: element => {
                    const termSpans = element.querySelectorAll('span.term[data-term="true"]');
                    return Array.from(termSpans).map(span => span.textContent || '') || [];
                },
                renderHTML: attributes => {
                    const terms = attributes.terms;
                    if (!terms || (Array.isArray(terms) && terms.length === 0)) {
                        return {};
                    }
                    return { 'data-terms': JSON.stringify(Array.isArray(terms) ? terms : [terms]) };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-definition="true"]',
                getAttrs: element => {
                    if (
                        !(element instanceof HTMLElement) ||
                        element.getAttribute('data-definition') !== 'true' ||
                        element.getAttribute('data-definition-mode') === 'block'
                    ) {
                        return false;
                    }
                    return {};
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyDefn', mergeAttributes(HTMLAttributes), 0];
    },

    addCommands() {
        return {
            // Insert a definition using HTML (most reliable method)
            insertDefinition:
                (options: InsertDefinitionOptions) =>
                ({ chain }) => {
                    const { terms, definition } = options;
                    let formattedDefinition = definition;
                    terms.forEach(term => {
                        formattedDefinition = formattedDefinition.replace(
                            term,
                            `<span class="term" data-term="true" obeycase="true">${term}</span>`,
                        );
                    });

                    // Build the HTML structure
                    const html = `<span data-definition="true">${formattedDefinition}</span>`;
                    return chain().insertContent(html).run();
                },

            // Insert definition with custom content (for complex cases with math, etc.)
            insertDefinitionWithContent:
                ({ content }: InsertDefinitionWithContentOptions) =>
                ({ state, dispatch }) => {
                    const pos = state.selection.from;
                    const fragment = Fragment.fromJSON(state.schema, content);
                    const zydefn = state.schema.nodes.zydefn.create({}, fragment);
                    const tr = state.tr.insert(pos, zydefn);
                    tr.setSelection(TextSelection.create(tr.doc, pos + zydefn.nodeSize - 1));
                    tr.scrollIntoView();
                    dispatch?.(tr);
                    return true;
                },

            /**
             * Hack to deal with double backspace required for deleting. There is no better way without making
             * larger changes.
             * Removes the innermost inline `zydefn` in one step when it is empty (or only
             * U+200B/whitespace). No-op if there is substantive text or any non-text inline.
             */
            removeEmptyInlineZydefn:
                () =>
                ({ state, editor }) => {
                    if (!state.selection.empty) return false;
                    const { $from } = state.selection;
                    const found = findInnermostZyDefnAtSelection($from);
                    if (!found) return false;
                    const { node } = found;
                    if (node.type.name !== 'zydefn' || !isTrulyEmptyInlineZydefn(node)) return false;
                    return editor.commands.removeZyDefn();
                },

            removeZyDefn:
                () =>
                ({ state, dispatch }) => {
                    const { $from } = state.selection;
                    const found = findInnermostZyDefnAtSelection($from);
                    if (!found) return false;
                    const { start, end, node } = found;
                    const tr = state.tr;
                    if (node.type.name === 'zydefn') {
                        tr.replaceWith(start, end, stripZydefTermsFromFragment(node.content));
                    } else {
                        const newBlocks: PmNode[] = [];
                        node.content.forEach((child: PmNode) => {
                            newBlocks.push(stripZydefTermsFromNode(child));
                        });
                        tr.replaceWith(start, end, Fragment.fromArray(newBlocks));
                    }
                    const mapPos = tr.mapping.map($from.pos, -1);
                    const resolved = tr.doc.resolve(Math.min(Math.max(mapPos, 0), tr.doc.content.size));
                    tr.setSelection(TextSelection.near(resolved, 1));
                    tr.scrollIntoView();
                    dispatch?.(tr);
                    return true;
                },
        };
    },

    addKeyboardShortcuts() {
        return {
            Backspace: ({ editor }: { editor: any }) => {
                return editor.commands.removeEmptyInlineZydefn();
            },
            'Alt-d': ({ editor }: { editor: any }) => {
                const { state } = editor;
                const { selection } = state;
                const { from, to, $from, $to, empty } = selection;

                if (editor.commands.removeZyDefn()) {
                    return true;
                }

                if (empty) {
                    return editor.commands.insertDefinitionWithContent({
                        content: [{ type: 'text', text: ' ' }],
                    });
                }

                const isSingleParentInlineSelection = $from.sameParent($to) && $from.parent.isTextblock;
                if (isSingleParentInlineSelection) {
                    const parent = $from.parent;
                    const relFrom = from - $from.start();
                    const relTo = to - $from.start();
                    const inlineFrag = parent.content.cut(relFrom, relTo);
                    const content = inlineFrag.toJSON();
                    return editor.chain().deleteSelection().insertDefinitionWithContent({ content }).run();
                }

                return editor.commands.setZyDefnBlock();
            },

            ArrowRight: ({ editor }: { editor: any }) => {
                const { state } = editor;
                const { selection } = state;
                const { $from, empty } = selection;

                if (!empty) return false;

                let insertPos: number | null = null;

                // Case 1: Cursor inside zydefn at end of its content
                for (let depth = $from.depth; depth > 0; depth--) {
                    if ($from.node(depth)?.type?.name === 'zydefn') {
                        const zyNode = $from.node(depth);
                        const cursorOffsetInZy = $from.pos - $from.start(depth);
                        if (cursorOffsetInZy !== zyNode.content.size) return false;
                        insertPos = $from.after(depth);
                        break;
                    }
                }

                // Case 2: Cursor immediately after zydefn (boundary in parent)
                if (insertPos === null && $from.nodeBefore?.type?.name === 'zydefn') {
                    insertPos = $from.pos;
                }

                if (insertPos === null) return false;

                const tr = state.tr.insertText(' ', insertPos);
                tr.setSelection(TextSelection.create(tr.doc, insertPos + 1));
                editor.view.dispatch(tr);
                return true;
            },

            Enter: ({ editor }: { editor: any }) => {
                const { state } = editor;
                const { selection } = state;
                const { $from } = selection;

                let zydefnDepth: number | null = null;
                let zydefTermDepth: number | null = null;
                for (let depth = $from.depth; depth > 0; depth--) {
                    const name = $from.node(depth)?.type?.name;
                    if (name === 'zydefTerm' && zydefTermDepth === null) {
                        zydefTermDepth = depth;
                    }
                    if (name === 'zydefn' || name === 'zydefnBlock') {
                        zydefnDepth = depth;
                        break;
                    }
                }

                if (zydefnDepth === null) return false;

                const zyNode = $from.node(zydefnDepth);
                if (zyNode.type.name === 'zydefnBlock') {
                    const paragraphType = state.schema.nodes.paragraph;
                    const zydefTermType = state.schema.nodes.zydefTerm;
                    if (!paragraphType) return false;

                    const paraDepth = zydefnDepth + 1;
                    const block = $from.node(paraDepth);
                    if (!block.isTextblock) return false;

                    // Jump out of zydefnBlock w/ Enter
                    if (block.content.size === 0) {
                        return editor.commands.unsetZyDefnBlock();
                    }

                    const from = $from.before(paraDepth);
                    const to = $from.after(paraDepth);
                    const paraStart = $from.start(paraDepth);
                    const cursorPosInDoc = $from.pos;
                    const cursorOffsetInPara = cursorPosInDoc - paraStart;

                    let beforeContent: Fragment;
                    let afterContent: Fragment;

                    if (zydefTermDepth !== null && zydefTermType) {
                        const termNode = $from.node(zydefTermDepth);
                        const termPos = $from.before(zydefTermDepth);
                        const termEnd = $from.after(zydefTermDepth);
                        const termContentStart = termPos + 1;
                        const cursorInTerm = cursorPosInDoc - termContentStart;

                        const termAttrs = termNode.attrs;
                        const termBeforeContent = termNode.content.cut(0, cursorInTerm);
                        const termAfterContent = termNode.content.cut(cursorInTerm);

                        const beforeTermOffset = termPos - paraStart;
                        const afterTermOffset = termEnd - paraStart;

                        const paraBeforeTermFrag = block.content.cut(0, beforeTermOffset);
                        const paraAfterTermFrag = block.content.cut(afterTermOffset);

                        const beforeParts: any[] = [];
                        paraBeforeTermFrag.forEach((n: any) => beforeParts.push(n));
                        if (termBeforeContent.size > 0) {
                            beforeParts.push(zydefTermType.create(termAttrs, termBeforeContent));
                        }

                        const afterParts: any[] = [];
                        if (termAfterContent.size > 0) {
                            afterParts.push(zydefTermType.create(termAttrs, termAfterContent));
                        }
                        paraAfterTermFrag.forEach((n: any) => afterParts.push(n));

                        beforeContent = Fragment.fromArray(beforeParts);
                        afterContent = Fragment.fromArray(afterParts);
                    } else {
                        beforeContent = block.content.cut(0, cursorOffsetInPara);
                        afterContent = block.content.cut(cursorOffsetInPara);
                    }

                    const attrs = block.attrs ? { ...block.attrs, id: null } : null;
                    const newPara1 = paragraphType.create(attrs, beforeContent.size > 0 ? beforeContent : undefined);
                    const newPara2 = paragraphType.create(attrs, afterContent.size > 0 ? afterContent : undefined);

                    const tr = state.tr;
                    if (!selection.empty) tr.deleteSelection();
                    tr.replaceWith(from, to, [newPara1, newPara2]);
                    tr.setSelection(TextSelection.create(tr.doc, from + newPara1.nodeSize));
                    tr.scrollIntoView();
                    editor.view.dispatch(tr);
                    return true;
                }

                const zydefnBlockType = state.schema.nodes.zydefnBlock;
                const paragraphType = state.schema.nodes.paragraph;
                const zydefTermType = state.schema.nodes.zydefTerm;
                if (!zydefnBlockType || !paragraphType) return false;

                const zyNodePos = $from.before(zydefnDepth);
                const zyNodeEnd = $from.after(zydefnDepth);

                const parentParaDepth = zydefnDepth - 1;
                const parentNode = $from.node(parentParaDepth);
                if (!parentNode?.isTextblock) return false;

                const paraContentStart = $from.start(parentParaDepth);
                const paraContentEnd = $from.end(parentParaDepth);

                const cursorPosInDoc = $from.pos;
                const zydefnContentStart = zyNodePos + 1;
                const cursorOffsetInZydefn = cursorPosInDoc - zydefnContentStart;

                let beforeContent: Fragment;
                let afterContent: Fragment;

                if (zydefTermDepth !== null && zydefTermType) {
                    // Cursor is inside a zydefTerm — split the term at the cursor.
                    const termNode = $from.node(zydefTermDepth);
                    const termPos = $from.before(zydefTermDepth);
                    const termEnd = $from.after(zydefTermDepth);
                    const termContentStart = termPos + 1;
                    const cursorInTerm = cursorPosInDoc - termContentStart;

                    const termAttrs = termNode.attrs;
                    const termBeforeContent = termNode.content.cut(0, cursorInTerm);
                    const termAfterContent = termNode.content.cut(cursorInTerm);

                    const beforeTermOffset = termPos - zydefnContentStart;
                    const afterTermOffset = termEnd - zydefnContentStart;

                    const zyBeforeTermFrag = zyNode.content.cut(0, beforeTermOffset);
                    const zyAfterTermFrag = zyNode.content.cut(afterTermOffset);

                    const beforeParts: any[] = [];
                    zyBeforeTermFrag.forEach((n: any) => beforeParts.push(n));
                    if (termBeforeContent.size > 0) {
                        beforeParts.push(zydefTermType.create(termAttrs, termBeforeContent));
                    }

                    const afterParts: any[] = [];
                    if (termAfterContent.size > 0) {
                        afterParts.push(zydefTermType.create(termAttrs, termAfterContent));
                    }
                    zyAfterTermFrag.forEach((n: any) => afterParts.push(n));

                    beforeContent = Fragment.fromArray(beforeParts);
                    afterContent = Fragment.fromArray(afterParts);
                } else {
                    // Cursor in zydefn but not inside a zydefTerm — simple content split.
                    beforeContent = zyNode.content.cut(0, cursorOffsetInZydefn);
                    afterContent = zyNode.content.cut(cursorOffsetInZydefn);
                }

                const innerNodes: any[] = [
                    paragraphType.create({ id: null, textAlign: null }, beforeContent.size > 0 ? beforeContent : undefined),
                    paragraphType.create({ id: null, textAlign: null }, afterContent.size > 0 ? afterContent : undefined),
                ];

                const zydefnBlockNode = zydefnBlockType.create(zyNode.attrs, Fragment.fromArray(innerNodes));

                const paraAttrs = parentNode.attrs ? { ...parentNode.attrs, id: null } : null;
                const outerBeforeFrag = state.doc.slice(paraContentStart, zyNodePos).content;
                const outerAfterFrag = state.doc.slice(zyNodeEnd, paraContentEnd).content;
                const outerBeforePara = outerBeforeFrag.size ? paragraphType.create(paraAttrs, outerBeforeFrag) : null;
                const outerAfterPara = outerAfterFrag.size ? paragraphType.create(paraAttrs, outerAfterFrag) : null;

                const replacement: any[] = [];
                if (outerBeforePara) replacement.push(outerBeforePara);
                replacement.push(zydefnBlockNode);
                if (outerAfterPara) replacement.push(outerAfterPara);

                const replaceFrom = $from.before(parentParaDepth);
                const replaceTo = $from.after(parentParaDepth);

                const tr = state.tr;
                tr.replaceWith(replaceFrom, replaceTo, Fragment.fromArray(replacement));

                // Place cursor at the start of the second paragraph inside zydefnBlock.
                const zydefnBlockPos = replaceFrom + (outerBeforePara ? outerBeforePara.nodeSize : 0);
                const secondParaStart = zydefnBlockPos + 1 + innerNodes[0].nodeSize + 1;
                tr.setSelection(TextSelection.create(tr.doc, secondParaStart));

                editor.view.dispatch(tr);
                return true;
            },
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: zydefnPluginKey,
                appendTransaction: (transactions, oldState, newState) => {
                    // Prevent infinite loops: if we appended a normalization transaction,
                    // it will come back through appendTransaction again.
                    if (transactions.some(tr => tr.getMeta(zydefnPluginKey))) {
                        return null;
                    }

                    if (!transactions.some(tr => tr.docChanged)) {
                        return null;
                    }

                    const insertedAnyBlock = transactions.some(transactionInsertsBlock);
                    // --- Upgrade: inline zydefn -> zydefnBlock when a block is inserted ---
                    if (insertedAnyBlock) {
                        // If the user wasn't inside an inline zydefn before the block insertion,
                        // we don't upgrade anything.
                        const oldZyDepth = findNodeDepthByName(oldState.selection.$from, 'zydefn');
                        if (oldZyDepth != null) {
                            const oldParentTextblockDepth = oldZyDepth - 1;
                            const $old = oldState.selection.$from;
                            const parentStart = $old.start(oldParentTextblockDepth);
                            const parentEnd = $old.end(oldParentTextblockDepth);
                            const blockInsertedInZydefnParagraph = transactions.some(tr =>
                                transactionInsertsBlockInRange(tr, parentStart, parentEnd),
                            );
                            if (!blockInsertedInZydefnParagraph) {
                                return null;
                            }
                            // Map the old selection position into the new document so we can find the
                            // corresponding inline zydefn to upgrade.
                            const mappedSelectionPos = mapPosThroughTransactions(oldState.selection.from, transactions, -1);
                            const $mapped = newState.doc.resolve(mappedSelectionPos);
                            const zyDepth = findNodeDepthByName($mapped, 'zydefn');
                            if (zyDepth != null) {
                                // Collect inserted block nodes (as they exist in newState), then move them
                                // into the upgraded zydefnBlock.
                                const insertedBlocks = collectInsertedBlockCandidates(transactions, newState);

                                // Heuristic fallback: if the selection is currently on a block node,
                                // treat that as the inserted block (common for toolbar insertions).
                                if (!insertedBlocks.length && (newState.selection as any)?.node?.isBlock) {
                                    insertedBlocks.push({
                                        pos: newState.selection.from,
                                        node: (newState.selection as any).node,
                                    });
                                }

                                const tr = newState.tr;
                                tr.setMeta(zydefnPluginKey, true);

                                // Delete inserted blocks from their current locations first (descending),
                                // then rebuild the paragraph containing the inline zydefn into:
                                // [beforeParagraph?] + zydefnBlock(paraBeforeCursor?, movedBlocks..., paraAfterCursor?) + [afterParagraph?]
                                for (const { pos } of [...insertedBlocks].sort((a, b) => b.pos - a.pos)) {
                                    const mappedPos = tr.mapping.map(pos, -1);
                                    const nodeAt = tr.doc.nodeAt(mappedPos);
                                    if (nodeAt?.isBlock) {
                                        tr.delete(mappedPos, mappedPos + nodeAt.nodeSize);
                                    }
                                }

                                const selectionPosAfterDeletes = tr.mapping.map(mappedSelectionPos, -1);
                                const $after = tr.doc.resolve(selectionPosAfterDeletes);
                                const zyDepthAfter = findNodeDepthByName($after, 'zydefn');
                                if (zyDepthAfter != null) {
                                    const zyNode = $after.node(zyDepthAfter);
                                    const zydefnBlockType = tr.doc.type.schema.nodes.zydefnBlock;
                                    const paragraphType = tr.doc.type.schema.nodes.paragraph;

                                    if (zydefnBlockType && paragraphType) {
                                        // Find the parent textblock (typically paragraph) that directly contains this zydefn.
                                        const parentTextblockDepth = zyDepthAfter - 1;
                                        const parentNode = $after.node(parentTextblockDepth);

                                        if (parentNode?.isTextblock) {
                                            const paraAttrs = parentNode.attrs ? { ...parentNode.attrs, id: null } : null;

                                            const paraContentStart = $after.start(parentTextblockDepth);
                                            const paraContentEnd = $after.end(parentTextblockDepth);
                                            const zyNodePos = $after.before(zyDepthAfter);
                                            const zyNodeEnd = $after.after(zyDepthAfter);

                                            const outerBeforeFrag = tr.doc.slice(paraContentStart, zyNodePos).content;
                                            const outerAfterFrag = tr.doc.slice(zyNodeEnd, paraContentEnd).content;

                                            const outerBeforePara = outerBeforeFrag.size
                                                ? paragraphType.create(paraAttrs, outerBeforeFrag)
                                                : null;
                                            const outerAfterPara = outerAfterFrag.size
                                                ? paragraphType.create(paraAttrs, outerAfterFrag)
                                                : null;

                                            // Split the zydefn inline content at the cursor so the inserted block(s)
                                            // land “inside” the definition at the correct location.
                                            const cursorOffsetInZydefn = Math.max(
                                                0,
                                                Math.min(selectionPosAfterDeletes - $after.start(zyDepthAfter), zyNode.content.size),
                                            );
                                            const zyBeforeFrag = zyNode.content.cut(0, cursorOffsetInZydefn);
                                            const zyAfterFrag = zyNode.content.cut(cursorOffsetInZydefn, zyNode.content.size);

                                            const innerNodes: any[] = [];
                                            if (zyBeforeFrag.size) {
                                                innerNodes.push(paragraphType.create({ id: null, textAlign: null }, zyBeforeFrag));
                                            }

                                            for (const { node } of insertedBlocks) {
                                                innerNodes.push(node);
                                            }

                                            if (zyAfterFrag.size) {
                                                innerNodes.push(paragraphType.create({ id: null, textAlign: null }, zyAfterFrag));
                                            }

                                            if (innerNodes.length === 0) {
                                                innerNodes.push(paragraphType.create({ id: null, textAlign: null }));
                                            }

                                            const zydefnBlockNode = zydefnBlockType.create(zyNode.attrs, Fragment.fromArray(innerNodes));

                                            const replacement: any[] = [];
                                            if (outerBeforePara) replacement.push(outerBeforePara);
                                            replacement.push(zydefnBlockNode);
                                            if (outerAfterPara) replacement.push(outerAfterPara);

                                            const replaceFrom = $after.before(parentTextblockDepth);
                                            const replaceTo = $after.after(parentTextblockDepth);
                                            tr.replaceWith(replaceFrom, replaceTo, Fragment.fromArray(replacement));

                                            // Put selection on the first moved block inside zydefnBlock when possible.
                                            // Otherwise put the cursor at the start of the zydefnBlock.
                                            const offsetBeforeZydefnBlock = outerBeforePara ? outerBeforePara.nodeSize : 0;
                                            const zydefnBlockPos = replaceFrom + offsetBeforeZydefnBlock;

                                            if (insertedBlocks.length) {
                                                // Compute the position of the first inserted block inside zydefnBlock.
                                                const firstInnerIsPara = zyBeforeFrag.size > 0;
                                                const innerOffset = firstInnerIsPara ? innerNodes[0].nodeSize : 0;
                                                const firstBlockPos = zydefnBlockPos + 1 + innerOffset;
                                                if (tr.doc.nodeAt(firstBlockPos)?.isBlock) {
                                                    tr.setSelection(NodeSelection.create(tr.doc, firstBlockPos));
                                                } else {
                                                    tr.setSelection(TextSelection.create(tr.doc, zydefnBlockPos + 1));
                                                }
                                            } else {
                                                tr.setSelection(TextSelection.create(tr.doc, zydefnBlockPos + 1));
                                            }

                                            return tr;
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // --- Downgrade: zydefnBlock -> inline zydefn when it becomes simple again ---
                    const downgradeCandidates = collectDowngradeCandidates(newState);
                    if (downgradeCandidates.length) {
                        const downgradeTr = newState.tr;
                        downgradeTr.setMeta(zydefnPluginKey, true);

                        const paragraphType = downgradeTr.doc.type.schema.nodes.paragraph;
                        const zydefnType = downgradeTr.doc.type.schema.nodes.zydefn;
                        if (paragraphType && zydefnType) {
                            const oldSelectionFrom = newState.selection.from;
                            const oldSelectionTo = newState.selection.to;

                            for (const { pos } of [...downgradeCandidates].sort((a, b) => b.pos - a.pos)) {
                                const mappedPos = downgradeTr.mapping.map(pos, -1);
                                const current = downgradeTr.doc.nodeAt(mappedPos);
                                if (!current || current.type.name !== 'zydefnBlock') continue;

                                if (!(current.childCount === 1 && current.child(0).type.name === 'paragraph')) continue;

                                const onlyParagraph = current.child(0);
                                const paraAttrs = onlyParagraph.attrs ? { ...onlyParagraph.attrs, id: null } : null;
                                const inlineZydefn = zydefnType.create(current.attrs, onlyParagraph.content);
                                const replacementPara = paragraphType.create(paraAttrs, inlineZydefn);

                                downgradeTr.replaceWith(mappedPos, mappedPos + current.nodeSize, replacementPara);
                            }

                            if (downgradeTr.steps.length) {
                                const mappedFrom = downgradeTr.mapping.map(oldSelectionFrom, -1);
                                const mappedTo = downgradeTr.mapping.map(oldSelectionTo, 1);
                                const $from = downgradeTr.doc.resolve(Math.min(mappedFrom, downgradeTr.doc.content.size));
                                const $to = downgradeTr.doc.resolve(Math.min(mappedTo, downgradeTr.doc.content.size));
                                downgradeTr.setSelection(TextSelection.between($from, $to));
                                return downgradeTr;
                            }
                        }
                    }

                    // --- Cleanup: remove empty zydefn nodes (e.g. from split when inserting block) ---
                    const emptyZydefnPositions: number[] = [];
                    newState.doc.descendants((node, pos) => {
                        if (node.type.name === 'zydefn' && node.content.size === 0) {
                            emptyZydefnPositions.push(pos);
                        }
                    });
                    if (emptyZydefnPositions.length) {
                        const cleanupTr = newState.tr;
                        cleanupTr.setMeta(zydefnPluginKey, true);
                        for (const pos of emptyZydefnPositions.sort((a, b) => b - a)) {
                            const node = cleanupTr.doc.nodeAt(pos);
                            if (node?.type.name === 'zydefn' && node.content.size === 0) {
                                cleanupTr.replaceWith(pos, pos + node.nodeSize, Fragment.empty);
                            }
                        }
                        return cleanupTr;
                    }

                    return null;
                },
            }),
        ];
    },
});

export const ZyDefn = ZyDefnHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyDefnNodeView);
    },
});

export default ZyDefn;
